import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import {
  Eye, EyeOff, Maximize2, Minimize2, RotateCcw,
} from 'lucide-react';
import { buildGeometry } from '../../lib/modelGeometry';
import { toSceneInPlace } from '../../pages/tools/instrumentsView';
import {
  TOOTH_ROWS, readableOn, buildGroups, isToothGroup, clampOpacity,
} from './viewerUtils';

const CONCURRENCY = 3;
const NO_GROUPS = {};

/** Ставит камеру так, чтобы в кадр вошли все модели. Для КТ в системе RAS — вид спереди, иначе — сбоку-сверху. */
const FitCamera = ({ geometries, token, orientation, rotation }) => {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);
  const done = useRef({ token: null, any: false });
  useEffect(() => {
    const list = Object.values(geometries);
    if (!list.length || !controls) return;
    if (done.current.any && done.current.token === token) return;
    const box = new THREE.Box3();
    list.forEach((g) => box.union(g.boundingBox));
    // модели повёрнуты кнопками «Повернуть»: габариты считаем с учётом поворота
    box.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(...rotation)));
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) / 2 || 50;
    const dist = (radius / Math.tan((camera.fov * Math.PI) / 360)) * 1.35;
    const dir = orientation === 'ras-zyx' ? new THREE.Vector3(0, 0, -1) : new THREE.Vector3(0.55, 0.4, 1).normalize();
    camera.position.copy(center).addScaledVector(dir, dist);
    camera.near = Math.max(dist / 200, 0.05);
    camera.far = dist * 30;
    camera.updateProjectionMatrix();
    controls.target.copy(center);
    controls.update();
    done.current = { token, any: true };
  }, [geometries, token, orientation, rotation, camera, controls]);
  return null;
};

const Scene = ({
  models, geometries, wire, rotation, fitToken, orientation,
}) => (
  <>
    <hemisphereLight args={['#ffffff', '#555566', 0.85]} />
    <directionalLight position={[100, 200, -300]} intensity={1.0} />
    <directionalLight position={[-100, -50, 300]} intensity={0.55} />
    <directionalLight position={[300, 100, 100]} intensity={0.35} />
    <group rotation={rotation}>
      {models.map((m) => (geometries[m.id] ? (
        <mesh key={m.id} geometry={geometries[m.id]} visible={m.visible}>
          <meshStandardMaterial color={m.color} roughness={0.55} metalness={0} side={THREE.DoubleSide}
                                wireframe={wire} transparent={m.opacity < 1} opacity={m.opacity}
                                depthWrite={m.opacity >= 1} />
        </mesh>
      ) : null))}
    </group>
    <OrbitControls makeDefault enableDamping={false} />
    <FitCamera geometries={geometries} token={fitToken} orientation={orientation} rotation={rotation} />
  </>
);

const iconBtn = 'inline-flex items-center justify-center gap-2 border-2 border-black bg-white px-3 py-2 text-sm font-bold '
  + 'hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-black';

const ToothCell = ({ fdi, item, on, loaded, color, onToggle, title }) => {
  if (!item) {
    return (
      <span title={`${fdi}: ${title}`} className="w-full aspect-square min-w-[2rem] flex items-center justify-center text-xs
        border-2 border-dashed border-gray-300 text-gray-300 select-none">{fdi}</span>
    );
  }
  return (
    <button type="button" onClick={onToggle} title={item.name} disabled={!loaded}
            style={on && loaded ? { background: color, color: readableOn(color) } : undefined}
            className={`w-full aspect-square min-w-[2rem] flex items-center justify-center text-xs font-bold border-2 transition
              ${on && loaded ? 'border-black' : 'border-gray-400 bg-white text-gray-500'} ${loaded ? 'hover:scale-105' : 'animate-pulse'}`}>
      {fdi}
    </button>
  );
};

const Pill = ({ item, on, loaded, failed, color, onToggle }) => (
  <button type="button" onClick={onToggle} disabled={!loaded} title={item.name}
          className={`flex items-center gap-2 border-2 px-2.5 py-1.5 text-sm text-left transition max-w-full
            ${on && loaded ? 'border-black bg-white font-bold shadow-[2px_2px_0_0_#000]' : 'border-gray-300 bg-gray-50 text-gray-500'}
            ${failed ? 'border-red-400 text-red-700' : ''} ${loaded || failed ? '' : 'animate-pulse'}`}>
      <span className="w-3.5 h-3.5 shrink-0 border border-black" style={{ background: on && loaded ? color : 'transparent' }} />
      <span className={`truncate ${on || !loaded ? '' : 'line-through'}`}>{item.name}</span>
  </button>
);

/**
 * Универсальный 3D-просмотрщик моделей. Используется для результата сегментации и для моделей пользователя.
 *
 * items: [{ id, name, group, color, fdi?, load: () => Promise<{ buffer: ArrayBuffer, format }> }]
 * groups: { [group]: { label, opacity?, hidden? } } — порядок и подписи групп в легенде
 * orientation: 'ras-zyx' (вершины сервиса: z, y, x в системе RAS) | 'none' (как в файле)
 * itemActions(item) / groupActions(group): дополнительные кнопки (скачать, сохранить, удалить)
 */
const ModelViewer = ({
  items, groups = NO_GROUPS, orientation = 'none', autoLoad = true, itemActions, groupActions,
}) => {
  const { t } = useTranslation();
  const [geometries, setGeometries] = useState({});
  const [state, setState] = useState({});                       // id -> 'loading' | 'done' | 'error'
  const [visible, setVisible] = useState({});
  const [opacity, setOpacity] = useState({});                   // группа -> 0.05…1
  const [colors, setColors] = useState({});                     // группа -> цвет, выбранный пользователем
  const [wire, setWire] = useState(false);
  const [rot, setRot] = useState([0, 0, 0]);                    // четверти оборота вокруг осей
  const [fitToken, setFitToken] = useState(0);
  const [isFs, setIsFs] = useState(false);
  const rootRef = useRef(null);
  const geomRef = useRef({});
  const attempted = useRef(new Set());
  const queue = useRef([]);
  const active = useRef(0);
  const alive = useRef(true);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const groupList = useMemo(() => buildGroups(items, groups), [items, groups]);
  const groupOf = useMemo(() => Object.fromEntries(groupList.map((g) => [g.key, g])), [groupList]);

  const finish = useCallback((id, status) => setState((s) => ({ ...s, [id]: status })), []);

  const pump = useCallback(() => {
    while (active.current < CONCURRENCY && queue.current.length) {
      const item = queue.current.shift();
      active.current += 1;
      (async () => {
        try {
          const { buffer, format } = await item.load();
          if (!alive.current || !itemsRef.current.some((x) => x.id === item.id)) return;
          const g = buildGeometry(buffer, format, orientation === 'ras-zyx' ? toSceneInPlace : undefined);
          geomRef.current = { ...geomRef.current, [item.id]: g };
          setGeometries(geomRef.current);
          finish(item.id, 'done');
        } catch (e) {
          if (alive.current) finish(item.id, 'error');
        } finally {
          active.current -= 1;
          if (alive.current) pump();
        }
      })();
    }
  }, [orientation, finish]);

  // Новые модели: запоминаем видимость и ставим в очередь на загрузку
  useEffect(() => {
    const fresh = items.filter((it) => !attempted.current.has(it.id));
    if (!fresh.length) return;
    fresh.forEach((it) => attempted.current.add(it.id));
    setVisible((v) => {
      const next = { ...v };
      fresh.forEach((it) => { next[it.id] = !groupOf[it.group]?.hidden; });
      return next;
    });
    if (autoLoad) {
      setState((s) => ({ ...s, ...Object.fromEntries(fresh.map((it) => [it.id, 'loading'])) }));
      queue.current.push(...fresh);
      pump();
    }
  }, [items, autoLoad, groupOf, pump]);

  // Удалённые модели: освобождаем память видеокарты
  useEffect(() => {
    const ids = new Set(items.map((it) => it.id));
    const gone = Object.keys(geomRef.current).filter((id) => !ids.has(id) && !ids.has(Number(id)));
    if (gone.length) {
      const next = { ...geomRef.current };
      gone.forEach((id) => { next[id].dispose(); delete next[id]; });
      geomRef.current = next;
      setGeometries(next);
    }
    queue.current = queue.current.filter((it) => ids.has(it.id));
    attempted.current.forEach((id) => { if (!ids.has(id)) attempted.current.delete(id); });
  }, [items]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      Object.values(geomRef.current).forEach((g) => g.dispose());
      geomRef.current = {};
    };
  }, []);

  useEffect(() => {
    const onChange = () => setIsFs(document.fullscreenElement === rootRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFs = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current?.requestFullscreen?.();
  };

  const groupOpacity = (key) => opacity[key] ?? groupOf[key]?.opacity ?? 1;
  const colorOf = (it) => colors[it.group] || it.color || '#cccccc';
  const models = items.map((it) => ({
    id: it.id, color: colorOf(it), opacity: groupOpacity(it.group), visible: visible[it.id] !== false,
  }));
  const setMany = (list, on) => setVisible((v) => {
    const next = { ...v };
    list.forEach((it) => { next[it.id] = on; });
    return next;
  });
  const rotate = (axis) => setRot((r) => r.map((q, i) => (i === axis ? (q + 1) % 4 : q)));
  const rotation = useMemo(() => rot.map((q) => (q * Math.PI) / 2), [rot]);
  const loadedCount = items.filter((it) => state[it.id] === 'done').length;
  const failedCount = items.filter((it) => state[it.id] === 'error').length;
  const loading = items.some((it) => state[it.id] === 'loading');

  return (
    <div ref={rootRef} className={isFs ? 'bg-white p-3 overflow-auto' : ''} data-testid="model-viewer">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button type="button" className={iconBtn} onClick={() => setFitToken((n) => n + 1)}>
          <RotateCcw size={16} />{t('pl.viewer.resetView')}
        </button>
        <button type="button" className={iconBtn} onClick={() => setMany(items, true)}>
          <Eye size={16} />{t('pl.viewer.showAll')}
        </button>
        <button type="button" className={iconBtn} onClick={() => setMany(items, false)}>
          <EyeOff size={16} />{t('pl.viewer.hideAll')}
        </button>
        <button type="button" className={`${iconBtn} ${wire ? 'bg-black text-white' : ''}`} onClick={() => setWire((w) => !w)}>
          {t('pl.viewer.wireframe')}
        </button>
        {orientation === 'none' && ['X', 'Y', 'Z'].map((a, i) => (
          <button key={a} type="button" className={iconBtn} onClick={() => rotate(i)} title={t('pl.viewer.rotateHint')}>
            {t('pl.viewer.rotate')} {a}
          </button>
        ))}
        <button type="button" className={`${iconBtn} ml-auto`} onClick={toggleFs}>
          {isFs ? <Minimize2 size={16} /> : <Maximize2 size={16} />}{isFs ? t('pl.viewer.exitFullscreen') : t('pl.viewer.fullscreen')}
        </button>
      </div>
      {(loading || failedCount > 0) && (
        <div className="flex flex-wrap gap-4 mb-2 text-sm">
          {loading && <span>{t('pl.viewer.loading', { done: loadedCount, total: items.length })}</span>}
          {failedCount > 0 && <span className="text-red-800 font-bold">{t('pl.viewer.failed', { count: failedCount })}</span>}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className={`border-2 border-black bg-[#1b1f24] ${isFs ? 'h-[calc(100vh-7rem)]' : 'h-[560px] lg:h-[740px]'}`}>
          <Canvas frameloop="demand" dpr={[1, 2]} camera={{ fov: 40, position: [0, 0, -300], up: [0, 1, 0] }}
                  gl={{ antialias: true, powerPreference: 'high-performance' }}>
            <Scene models={models} geometries={geometries} wire={wire} rotation={rotation}
                   fitToken={fitToken} orientation={orientation} />
          </Canvas>
        </div>

        <div className={`space-y-3 overflow-auto pr-1 ${isFs ? 'max-h-[calc(100vh-7rem)]' : 'xl:max-h-[740px]'}`}>
          {groupList.map((g) => {
            const on = g.items.filter((it) => visible[it.id] !== false).length;
            const allOn = on === g.items.length;
            const first = g.items[0];
            const single = g.items.length === 1;
            const swatch = colors[g.key] || first.color || '#cccccc';
            const tooth = isToothGroup(g);
            return (
              <section key={g.key} className="border-2 border-black bg-white">
                <header className="flex items-center gap-3 px-3 py-2 bg-[#F5F1E8] border-b-2 border-black">
                  <label className="relative w-7 h-7 shrink-0 border-2 border-black cursor-pointer" style={{ background: swatch }}
                         title={t('pl.viewer.color')}>
                    <input type="color" value={/^#[0-9a-f]{6}$/i.test(swatch) ? swatch : '#cccccc'}
                           onChange={(e) => setColors((c) => ({ ...c, [g.key]: e.target.value }))}
                           className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" aria-label={t('pl.viewer.color')} />
                  </label>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-base truncate" title={g.label}>{g.label}</div>
                    {!single && <div className="text-xs text-gray-600">{t('pl.viewer.shown', { on, total: g.items.length })}</div>}
                  </div>
                  {single && groupActions && groupActions(g, first)}
                  <button type="button" onClick={() => setMany(g.items, !allOn)} disabled={single && state[first.id] !== 'done'}
                          className="p-2 border-2 border-black bg-white hover:bg-black hover:text-white disabled:opacity-40"
                          title={allOn ? t('pl.viewer.hideGroup') : t('pl.viewer.showGroup')}>
                    {allOn ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </header>
                <div className="px-3 py-3 space-y-3">
                  <label className="block text-sm">
                    <span className="flex justify-between"><span>{t('pl.viewer.opacity')}</span>
                      <span className="tabular-nums font-bold">{Math.round(groupOpacity(g.key) * 100)}%</span></span>
                    <input type="range" min="0.05" max="1" step="0.05" value={groupOpacity(g.key)}
                           onChange={(e) => setOpacity((o) => ({ ...o, [g.key]: clampOpacity(e.target.value) }))}
                           className="w-full h-2 accent-[#C41E3A] cursor-pointer" />
                  </label>
                  {tooth && (
                    <div className="space-y-1">
                      {TOOTH_ROWS.map((row, r) => (
                        <div key={r} className="grid gap-1" style={{ gridTemplateColumns: 'repeat(16, minmax(0, 1fr))' }}>
                          {row.map((fdi) => {
                            const it = g.items.find((x) => x.fdi === fdi);
                            return (
                              <ToothCell key={fdi} fdi={fdi} item={it} title={t('pl.viewer.toothAbsent')}
                                         on={it ? visible[it.id] !== false : false} loaded={it ? state[it.id] === 'done' : false}
                                         color={it ? colorOf(it) : '#ccc'}
                                         onToggle={() => it && setVisible((v) => ({ ...v, [it.id]: v[it.id] === false }))} />
                            );
                          })}
                        </div>
                      ))}
                      <p className="text-xs text-gray-600">{t('pl.viewer.toothChartHint')}</p>
                    </div>
                  )}
                  {!tooth && !single && (
                    <div className="flex flex-wrap gap-2">
                      {g.items.map((it) => (
                        <div key={it.id} className="flex items-center gap-1 max-w-full">
                          <Pill item={it} on={visible[it.id] !== false} loaded={state[it.id] === 'done'}
                                failed={state[it.id] === 'error'} color={colorOf(it)}
                                onToggle={() => setVisible((v) => ({ ...v, [it.id]: v[it.id] === false }))} />
                          {itemActions && itemActions(it)}
                        </div>
                      ))}
                    </div>
                  )}
                  {single && state[first.id] === 'error' && <p className="text-sm text-red-800 font-bold">{t('pl.viewer.itemFailed')}</p>}
                  {single && state[first.id] === 'loading' && <p className="text-sm text-gray-600 animate-pulse">{t('pl.viewer.itemLoading')}</p>}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <p className="text-sm text-gray-600 mt-3">
        {orientation === 'ras-zyx' ? t('pl.viewer.hintRas') : t('pl.viewer.hintFree')}
      </p>
    </div>
  );
};

export default ModelViewer;
